(function () {
    const THIRTY_SECONDS = 30000;
    const orderUpOrderStates = {
        notified: 1,
        accepted: 2,
        declined: 3,
        captured: 4,
        discarded: 5,
        timeout: 6,
        completed: 7,
        waitingforcapture: 8
    };
    const orderUpStoreStatus = {
        online: 1,
        offline: 2
    };
    const orderUpEvents = {
        newOrder: "new-order",
        declinedOrder: "declined-order",
        acceptedOrder: "accepted-order",
        capturedOrder: "captured-order",
        completedOrder: "completed-order",
        timedOutOrder: "timedout-order",
        storeOnline: "store-online",
        storeOffline: "store-offline",
        channelEnabled: "store-channel-enabled",
        channelDisabled: "store-channel-disabled"
    };
    const socketStatus = {
        Opening: "opening",
        Connecting: "connecting",
        Connected: "connected",
        Disconnected: "disconnected",
    };

    class OrderUpOrdersService {
        constructor(
            apiService,
            DashboardConfig,
            $state
        ) {
            this._apiService = apiService;
            this._dashboardConfig = DashboardConfig;
            this._state = $state;
            this._socketReconnectTimeout = null;
            this._socketManuallyDisconnected = false;
            this._eventHandlers = {};
        }

        isInStates(o, expected) {
            return o &&
                o.OrderUpOrder &&
                expected.includes(o.OrderUpOrder.StateId);
        }

        chuck(message) {
            return Promise.reject(new Error(message));
        }

        get _rtapiServer() {
            return this._dashboardConfig.RTAPIServer;
        }

        get _rtapiPath() {
            return this._dashboardConfig.RTAPIPath;
        }

        poll(storeId) {
            return this._apiService.OrderUp.getOrders(storeId);
        }

        acceptOrder(orderGuid, delayTime = 0) {
            return this._apiService.OrderUp.acceptOrder(orderGuid, delayTime);
        }

        declineOrder(
            orderGuid,
            rejectReason) {
            return this._apiService.OrderUp.declineOrder(
                orderGuid,
                rejectReason);
        }

        removeOrder(orderGuid) {
            return this._apiService.OrderUp.removeOrder(
                orderGuid);
        }

        captureOrder(orderGuid) {
            return this._apiService.OrderUp.captureOrder(
                orderGuid);
        }

        markOrderReady(orderGuid) {
            return this._apiService.OrderUp.markOrderReady(
                orderGuid);
        }

        enableHeartbeat(
            storeId,
            callback,
            failureCallback
        ) {
            this._beatOnce(
                storeId,
                callback,
                failureCallback
            );
            this._heartbeatTimer = window.setInterval(
                () => {
                    this._beatOnce(
                        storeId,
                        callback,
                        failureCallback
                    );
                },
                THIRTY_SECONDS
            );
        }

        _beatOnce(storeId, callback, failureCallback) {
            if (this._beatPromise) {
                console.warn("already waiting on heartbeat!");
                return this._beatPromise;
            }
            this._beatPromise = this._apiService.OrderUp.heartbeat(storeId)
                .then((result) => {
                    if (typeof callback === "function") {
                        callback(result.data);
                    }
                    return result;
                })
                .catch((err) => {
                    this.disconnectRTAPI();
                    if (typeof failureCallback === "function") {
                        failureCallback();
                    }
                    throw err;
                })
                .finally(() => {
                    this._beatPromise = undefined;
                });
            return this._beatPromise;
        }

        disableHeartbeat() {
            if (this._heartbeatTimer) {
                window.clearInterval(this._heartbeatTimer);
                this._heartbeatTimer = undefined;
            }
        }

        toggleStoreChannelStatus(
            storeId,
            channelIsEnabled,
        ) {
            if (!channelIsEnabled) {
                this.disableHeartbeat();
            }
            const query = channelIsEnabled
                ? this._apiService.OrderUp.enableOrderUpChannel(storeId)
                : this._apiService.OrderUp.disableOrderUpChannel(storeId);

            return query.then(result => result.data);
        }

        enableOrderUpChannel(storeId) {
            this._apiService.OrderUp.enableOrderUpChannel(storeId);
        }

        disableOrderUpChannel(storeId) {
            this._apiService.OrderUp.disableOrderUpChannel(storeId);
        }

        isRTAPIConnecting() {
            return this.socketStatus() === socketStatus.Connecting;
        }

        isRTAPIConnected() {
            return this.socketStatus() === socketStatus.Connected;
        }

        connectRTAPI() {
            if (typeof window.io !== "function") {
                return this.chuck(`Socket.IO is not loaded`);
            }
            if (!(this._rtapiServer || "").trim()) {
                return this.chuck(`RTAPIServer url is not defined (check environment variables)`);
            }
            this._storeId = parseInt(this._state.params.storeId);
            if (isNaN(this._storeId)) {
                return this.chuck(`current $state has no store id`);
            }
            if (this.isRTAPIConnecting() || this.isRTAPIConnected()) {
                console.log("Socket is already active:", this.socketStatus());
                return;
            }
            return this._apiService.OrderUp.fetchRTAPISessionDetails(this._storeId)
                .then(details => this._tryConnectToRTAPI(details.data));
        }

        disconnectRTAPI() {
            if (!this.isRTAPIConnected() && !this.isRTAPIConnecting()) {
                console.warn("Socket is not active, can't disconnect.");
                return;
            }
            this._socketManuallyDisconnected = true;
            try {
                this._socket.disconnect();
            } catch (e) {
                console.warn(`Error disconnecting socket: ${e}`);
                this._socketManuallyDisconnected = false;
            }
            this._socket = undefined;
            this._eventHandlers = [];
            this._clearEventListeners();
            this._clearSocketReconnectTimeout();
        }

        _clearSocketReconnectTimeout() {
            if (!this._socketReconnectTimeout) {
                return;
            }
            clearTimeout(this._socketReconnectTimeout);
        }

        on(ev, callback) {
            if (!this._eventHandlers[ev]) {
                this._eventHandlers[ev] = [];
            }
            this._eventHandlers[ev].push(callback);
        }

        _tryConnectToRTAPI(sessionInfo) {
            const
                sessionId = ((sessionInfo || {}).SessionId || "").trim(),
                brandId = parseInt((sessionInfo || {}).BrandId);
            if (!sessionId) {
                return this.chuck(`You must log in to be able to use real-time messaging within Order-Up`);
            }
            if (isNaN(brandId)) {
                return this.chuck(`Unable to set up real-time messaging: brand unknown`);
            }
            return new Promise((resolve, reject) => {
                try {
                    this._brandId = brandId;
                    this._socket = window.io(
                        this._rtapiServer,
                        {
                            transports: ["websocket", "polling"],
                            upgrade: true,
                            reconnection: true,
                            reconnectionAttempts: Infinity,
                            reconnectionDelay: 500,
                            reconnectionDelayMax: 15000,
                            randomizationFactor: 0.5,
                            timeout: 20000,
                            autoConnect: true,
                            forceNew: false,
                            path: this._rtapiPath,
                            query: {
                                SessionId: sessionId
                            },
                        }
                    );
                    this._socket.on("connect", () => this._onSocketConnect(resolve));
                    this._socket.on("connect_error", e => this._onSocketConnectError(e, reject));
                    this._socket.on("disconnect", info => this._onSocketDisconnect(info));
                    this._socket.on("error", e => this._onSocketError(e, reject));
                    this._setupEventListeners();
                } catch (e) {
                    reject(e);
                }
            });
        }

        socketStatus() {
            if (this._socket?.connected) {
                return socketStatus.Connected;
            }
            const opening = this._socket?.io?.engine?.readyState === socketStatus.Opening;
            if (this._socket?.active || opening) {
                return socketStatus.Connecting;
            }
            return socketStatus.Disconnected;
        }

        _onSocketConnectError(e, reject) {
            console.error("socket connect error", e);
            reject(e);
        }

        _onSocketDisconnect(info) {
            console.warn("socket disconnect", info);
            this._emitClientEvent(orderUpEvents.storeOffline, "Real-Time messaging is disconnected")
                .then(() => { /* fire-and-forget */ });
            if (!this._socketManuallyDisconnected) {
                this._socketReconnectTimeout = setTimeout(this._reconnectSocket.bind(this), 1000);
                return;
            }
            this._socketManuallyDisconnected = false;
        }

        _onSocketError(e, reject) {
            console.error("socket error", e);
            reject(e);
        }

        _onSocketConnect(done) {
            this._emitClientEvent(orderUpEvents.storeOnline, null)
                .then(() => { /* fire-and-forget */ });
            const subscriptionRequest = {
                StoreId: this._storeId
            };
            // NB: subscriptions must happen in series, not parallel
            return this._subscribeToOrderUpStoreEvents(subscriptionRequest)
                .then(() => this._subscribeToOrderUpStoreChannelEvents(subscriptionRequest))
                .then(() => this._subscribeToOrderUpOrderEvents(subscriptionRequest))
                .then(() => done())
                .catch(e => console.error(e));
        }

        _setupEventListeners() {
            window.addEventListener("focus", this._setWindowForegrounded.bind(this), false);
            window.addEventListener("pageshow", this._setWindowForegrounded.bind(this), false);
        }

        _clearEventListeners() {
            window.removeEventListener("focus", this._setWindowForegrounded.bind(this), false);
            window.removeEventListener("pageshow", this._setWindowForegrounded.bind(this), false);
        }

        _setWindowForegrounded() {
            this._reconnectSocket();
        }

        _reconnectSocket() {
            if (!this._socket) {
                return;
            }
            try {
                this._socket.connect();
            } catch (e) {
                console.error(e);
                this._socketReconnectTimeout = setTimeout(this._reconnectSocket, 1000);
            }
        }

        _subscribeToOrderUpOrderEvents(request) {
            this._emit(
                "/stream_orderup",
                request
            ).then(() => this._setupOrderUpOrderEventHandler());
        }

        _setupOrderUpOrderEventHandler() {
            this._socket.on(`/orderup_${this._brandId}`, (messages, callback) => {
                const
                    orders = messages.map(m => JSON.parse(m.data)).filter(o => !!o),
                    newOrders = orders.filter(o => this.isInStates(o, [orderUpOrderStates.notified])),
                    declinedOrders = orders.filter(o => this.isInStates(o, [orderUpOrderStates.declined])),
                    acceptedOrders = orders.filter(o => this.isInStates(o, [
                        orderUpOrderStates.accepted,
                        orderUpOrderStates.waitingforcapture
                    ])),
                    capturedOrders = orders.filter(o => this.isInStates(o, [orderUpOrderStates.captured])),
                    completedOrders = orders.filter(o => this.isInStates(o, [orderUpOrderStates.completed])),
                    timedOutOrders = orders.filter(o => this.isInStates(o, [orderUpOrderStates.timeout])),
                    validStates = newOrders
                        .concat(declinedOrders)
                        .concat(acceptedOrders)
                        .concat(capturedOrders)
                        .concat(completedOrders)
                        .concat(timedOutOrders),
                    erroneousOrders = orders.filter(o => validStates.indexOf(o) === -1),
                    promises = [];

                if (erroneousOrders.length) {
                    const
                        len = erroneousOrders.length,
                        s = len === 1 ? "" : "s";
                    console.error(`${len} order${s} with invalid state were pushed`, erroneousOrders);
                }

                const queue = [
                    [ orderUpEvents.newOrder, newOrders ],
                    [ orderUpEvents.declinedOrder, declinedOrders ],
                    [ orderUpEvents.acceptedOrder, acceptedOrders ],
                    [ orderUpEvents.capturedOrder, capturedOrders ],
                    [ orderUpEvents.completedOrder, completedOrders ],
                    [ orderUpEvents.timedOutOrder, timedOutOrders ]
                ];
                for (let pair of queue) {
                    const [ ev, data ] = pair;
                    for (let orderInfo of data) {
                        promises.push(this._emitClientEvent(ev, orderInfo.Order));
                    }
                }

                Promise.all(promises)
                    .then(() => callback());
            });
        }

        _subscribeToOrderUpStoreEvents(request) {
            return new Promise((resolve, reject) => {
                this._emit(
                    "/stream_orderup_store_status",
                    request
                ).then(this._setupOrderUpStoreEventHandler.bind(this))
                    .then(() => resolve())
                    .catch(e => reject(e));
            });
        }

        _subscribeToOrderUpStoreChannelEvents(request) {
            return new Promise((resolve, reject) => {
                this._emit(
                    "/stream_orderup_store_channel_status",
                    request
                ).then(this._setupOrderUpStoreChannelEventHandler.bind(this))
                    .then(() => resolve())
                    .catch(e => reject(e));
            });
        }

        _setupOrderUpStoreChannelEventHandler(streamResult) {
            if (!streamResult || !streamResult.Success) {
                throw new Error(`Unable to subscribe for order-up store channelevents`);
            }
            this._socket.on("/orderup_store_channel_status", (messages, callback) => {
                // only actually care about the last status event (ie, current state)
                const
                    last = messages[messages.length - 1],
                    data = JSON.parse(last.data),
                    clientEvent = data.ChannelIsEnabled
                        ? orderUpEvents.channelEnabled
                        : orderUpEvents.channelDisabled;
                this._emitClientEvent(clientEvent, data.Reason || "")
                    .then(() => callback());
            });
        }

        _setupOrderUpStoreEventHandler(streamResult) {
            if (!streamResult || !streamResult.Success) {
                throw new Error(`Unable to subscribe for order-up store events`);
            }
            this._socket.on("/orderup_store_status", (messages, callback) => {
                // only actually care about the last status event (ie, current state)
                const
                    last = messages[messages.length - 1],
                    data = JSON.parse(last.data),
                    clientEvent = data.IsOnline
                        ? orderUpEvents.storeOnline
                        : orderUpEvents.storeOffline;
                this._emitClientEvent(clientEvent, data.Reason || "")
                    .then(() => callback());
            });
        }

        _emitClientEvent(ev, data) {
            const
                handlers = this._snapshotHandlersFor(ev),
                results = handlers.map(handler => {
                    try {
                        return handler(data);
                    } catch (e) {
                        this._log(`Event handler for ${ev} threw error:`, e);
                        return Promise.reject();
                    }
                }).map(r => {
                    // if any handlers are async, we should wait for them to complete
                    if (r && typeof r.then === "function") {
                        return r;
                    }
                    // if a result is returned synchronously, wrap in a resolved
                    // promise to simplify the final result handling
                    return Promise.resolve(r);
                });

            return Promise.all(results);
        }

        _log() {
            if (this._dashboardConfig.LogLevel === "DEBUG") {
                console.log.apply(console, Array.from(arguments));
            }
        }

        _snapshotHandlersFor(ev) {
            const handlers = this._eventHandlers[ev] || [];
            return handlers.slice();
        }

        _emit(eventName, data) {
            return new Promise((_resolve, _reject) => {
                let completed = false,
                    timer;
                const
                    isCompleted = () => {
                        if (completed) {
                            return true;
                        }
                        completed = true;
                        return false;
                    },
                    resolve = result => {
                        clearTimeout(timer);
                        if (isCompleted()) {
                            return;
                        }
                        _resolve(result);
                    }, reject = err => {
                        if (isCompleted()) {
                            return;
                        }
                        _reject(err);
                    };
                this._socket.emit(eventName, data, resolve);
                timer = setTimeout(() => {
                    console.error("emit timed out", {
                        eventName,
                        data
                    });
                    reject(
                        `timed out attempting to emit ${eventName}`
                    );
                }, 5000);
            });
        }
    }

    angular.module("dashboard")
        .service(
            "OrderUpOrdersService",
            [
                "APIService",
                "DashboardConfig",
                "$state",
                OrderUpOrdersService
            ]
        );

    angular.module("dashboard")
        .constant(
            "OrderUpLookup",
            {
                orderUpStoreStatus,
                orderUpOrderStates,
                orderUpEvents
            }
        );
})();
